import React, { useState, useEffect } from 'react';
import { Circle } from 'lucide-react';
import { findBestMove } from '../../utils/checkersAI';

interface CheckersProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onWin: () => void;
}

type Piece = null | 'player' | 'ai' | 'player-king' | 'ai-king';
type Board = Piece[][];

export function Checkers({ difficulty, onWin }: CheckersProps) {
  const BOARD_SIZE = 8;
  const [board, setBoard] = useState<Board>(() => {
    const initialBoard: Board = Array(BOARD_SIZE).fill(null)
      .map(() => Array(BOARD_SIZE).fill(null));
    
    // Set up AI pieces (top)
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if ((row + col) % 2 === 1) {
          initialBoard[row][col] = 'ai';
        }
      }
    }
    
    // Set up player pieces (bottom)
    for (let row = BOARD_SIZE - 3; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if ((row + col) % 2 === 1) {
          initialBoard[row][col] = 'player';
        }
      }
    }
    
    return initialBoard;
  });
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [selectedPiece, setSelectedPiece] = useState<[number, number] | null>(null);
  const [validMoves, setValidMoves] = useState<{row: number, col: number}[]>([]);
  const [gameOver, setGameOver] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);

  const getPieceColor = (piece: Piece) => {
    if (!piece) return null;
    return piece.startsWith('player') ? 'text-cyan-400' : 'text-pink-500';
  };

  const getValidMoves = (row: number, col: number) => {
    const piece = board[row][col];
    if (!piece?.startsWith('player')) return [];

    const moves: {row: number, col: number}[] = [];
    const directions = piece === 'player-king' ? [-1, 1] : [-1];

    // Regular moves
    for (const dRow of directions) {
      for (const dCol of [-1, 1]) {
        const newRow = row + dRow;
        const newCol = col + dCol;
        
        if (newRow >= 0 && newRow < BOARD_SIZE && 
            newCol >= 0 && newCol < BOARD_SIZE && 
            !board[newRow][newCol]) {
          moves.push({ row: newRow, col: newCol });
        }
      }
    }

    // Captures
    for (const dRow of directions.map(d => d * 2)) {
      for (const dCol of [-2, 2]) {
        const newRow = row + dRow;
        const newCol = col + dCol;
        const jumpedRow = row + dRow/2;
        const jumpedCol = col + dCol/2;
        
        if (newRow >= 0 && newRow < BOARD_SIZE && 
            newCol >= 0 && newCol < BOARD_SIZE && 
            !board[newRow][newCol] &&
            board[jumpedRow][jumpedCol]?.startsWith('ai')) {
          moves.push({ row: newRow, col: newCol });
        }
      }
    }

    return moves;
  };

  const makeAIMove = () => {
    setAiThinking(true);
    const move = findBestMove(board, difficulty);
    
    if (move) {
      const newBoard = board.map(row => [...row]);
      
      if (move.captures) {
        move.captures.forEach(capture => {
          newBoard[capture.row][capture.col] = null;
        });
      }

      newBoard[move.toRow][move.toCol] = newBoard[move.fromRow][move.fromCol];
      newBoard[move.fromRow][move.fromCol] = null;

      // King promotion
      if (move.toRow === 7 && newBoard[move.toRow][move.toCol] === 'ai') {
        newBoard[move.toRow][move.toCol] = 'ai-king';
      }

      setBoard(newBoard);
    }
    
    setAiThinking(false);
    setIsPlayerTurn(true);
  };

  const handleClick = (row: number, col: number) => {
    if (!isPlayerTurn || gameOver || aiThinking) return;

    const piece = board[row][col];
    
    // Select a piece
    if (piece?.startsWith('player')) {
      setSelectedPiece([row, col]);
      setValidMoves(getValidMoves(row, col));
      return;
    }

    // Move selected piece
    if (selectedPiece && validMoves.some(move => move.row === row && move.col === col)) {
      const [fromRow, fromCol] = selectedPiece;
      const newBoard = board.map(row => [...row]);
      
      // Handle captures
      if (Math.abs(row - fromRow) === 2) {
        const jumpedRow = (fromRow + row) / 2;
        const jumpedCol = (fromCol + col) / 2;
        newBoard[jumpedRow][jumpedCol] = null;
      }

      newBoard[row][col] = newBoard[fromRow][fromCol];
      newBoard[fromRow][fromCol] = null;

      // King promotion
      if (row === 0 && newBoard[row][col] === 'player') {
        newBoard[row][col] = 'player-king';
      }

      setBoard(newBoard);
      setSelectedPiece(null);
      setValidMoves([]);
      setIsPlayerTurn(false);
    }
  };

  useEffect(() => {
    if (!isPlayerTurn && !gameOver && !aiThinking) {
      const timer = setTimeout(makeAIMove, 1000);
      return () => clearTimeout(timer);
    }
  }, [isPlayerTurn, gameOver, board, difficulty]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="bg-gray-800 p-4 rounded-lg">
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className="flex">
            {row.map((piece, colIndex) => (
              <button
                key={`${rowIndex}-${colIndex}`}
                className={`w-12 h-12 flex items-center justify-center
                  ${(rowIndex + colIndex) % 2 === 0 ? 'bg-gray-700' : 'bg-gray-600'}
                  ${selectedPiece?.[0] === rowIndex && selectedPiece?.[1] === colIndex ? 'ring-2 ring-yellow-400' : ''}
                  ${validMoves.some(move => move.row === rowIndex && move.col === colIndex) ? 'ring-2 ring-green-400' : ''}
                  ${aiThinking ? 'cursor-not-allowed' : ''}
                `}
                onClick={() => handleClick(rowIndex, colIndex)}
                disabled={aiThinking}
              >
                {piece && (
                  <Circle
                    className={`w-8 h-8 ${getPieceColor(piece)} ${
                      piece.endsWith('-king') ? 'ring-2 ring-yellow-400' : ''
                    } ${aiThinking ? 'opacity-50' : ''}`}
                    fill="currentColor"
                  />
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="text-cyan-400 font-mono">
        {gameOver ? (
          <span className="text-xl">Game Over!</span>
        ) : aiThinking ? (
          <span className="animate-pulse">AI Thinking...</span>
        ) : (
          <span>{isPlayerTurn ? 'Your Turn' : 'AI Turn'}</span>
        )}
      </div>
    </div>
  );
}